import React, { useEffect, useState, useMemo } from "react";
import { Backdrop } from "../ui/Modal";
import { client } from "../../api/client";
import type { ColumnMeta, ILnp } from "../../type";
import Dropdown from "../ui/dropdown";
import LoadingSpinner from "../ui/LoadingSpinner";

interface ImportJob {
  id: number;
  filename: string;
  total_rows: number;
  valid_rows: number;
  created_at: string;
  status: string;
}

interface Props {
  mode: "batch" | "selected";
  selectedPks?: Set<number>;
  rows?: ILnp[];
  originalRows?: Record<number, ILnp>;
  onClose: () => void;
  columns: ColumnMeta[];
  setCell: (
    pk: number,
    field: keyof ILnp,
    value: ILnp[keyof ILnp],
    serverRow: ILnp
  ) => void;
  showInfo: (config: {
    title: string;
    message: React.ReactNode;
    variant?: "success" | "info" | "warning";
  }) => void;
  metadataList: { experiment_id: string; title: string }[];
}

const BulkEditModal: React.FC<Props> = ({
  mode,
  selectedPks,
  rows = [],
  originalRows = {},
  onClose,
  columns,
  setCell,
  showInfo,
  metadataList,
}) => {
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [applying, setApplying] = useState(false);

  const [selectedJobId, setSelectedJobId] = useState<string>("");
  const [selectedField, setSelectedField] = useState<string>("");
  const [newValue, setNewValue] = useState<string>("");

  useEffect(() => {
    if (mode === "batch") {
      const fetchJobs = async () => {
        setLoadingJobs(true);
        try {
          const resp = await client.get("/search/admin/import/?status=completed");
          setJobs(resp.data.results || resp.data);
        } catch (err) {
          console.error("Failed to load jobs", err);
        } finally {
          setLoadingJobs(false);
        }
      };
      fetchJobs();
    }
  }, [mode]);

  const editableColumns = useMemo(() => {
    return columns.filter((c) => c.editable && c.name !== "index" && c.name !== "lnp_id");
  }, [columns]);

  const selectedColumn = useMemo(() => {
    return columns.find((c) => c.name === selectedField);
  }, [selectedField, columns]);

  const handleApply = async () => {
    if (!selectedField) return;

    setApplying(true);
    let totalUpdated = 0;
    try {
      const applyValueToRow = (row: ILnp) => {
        let valToSet: any = newValue;
        if (selectedColumn?.type === "boolean") {
          valToSet = newValue === "true";
        } else if (selectedColumn?.type === "integer" || selectedColumn?.type === "number") {
          valToSet = newValue === "" ? null : Number(newValue);
        } else if (newValue === "") {
          valToSet = null;
        }
        setCell(row.index, selectedField as keyof ILnp, valToSet, row);
      };

      if (mode === "batch") {
        if (!selectedJobId) return;
        let page = 1;
        while (true) {
          const resp = await client.get(
            `/search/admin/lnp/?filter={"import_job_id":${selectedJobId}}&page=${page}&page_size=200`
          );
          const data = resp.data.results || resp.data;
          if (data.length === 0) break;

          data.forEach(applyValueToRow);
          totalUpdated += data.length;
          if (data.length < 200) break;
          page++;
        }
      } else {
        // mode === "selected"
        if (!selectedPks) return;
        selectedPks.forEach(pk => {
          const row = rows.find(r => r.index === pk) || originalRows[pk] || { index: pk } as ILnp;
          applyValueToRow(row);
        });
        totalUpdated = selectedPks.size;
      }

      showInfo({
        title: "Bulk Edit Applied",
        message: `Successfully marked ${totalUpdated} row(s) with new pending changes. Click "Review & save" to commit.`,
        variant: "success",
      });
      onClose();
    } catch (err) {
      console.error(err);
      showInfo({
        title: "Error",
        message: "Failed to apply changes to all rows.",
        variant: "warning",
      });
    } finally {
      setApplying(false);
    }
  };

  return (
    <Backdrop onClose={onClose} size="md">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">
        {mode === "batch" ? "Bulk Edit by Batch" : "Bulk Edit Selected Rows"}
      </h3>
      <p className="mb-6 text-sm text-gray-600">
        {mode === "batch"
          ? "Select an upload batch and a field to update. The chosen value will be applied to all rows within the batch as a pending change."
          : `Select a field to update. The chosen value will be applied to the ${selectedPks?.size || 0} selected row(s) as a pending change.`}
      </p>

      <div className="space-y-4">
        {mode === "batch" && (
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Target Batch
            </label>
            {loadingJobs ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <LoadingSpinner /> Loading batches...
              </div>
            ) : (
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- Select an upload batch --</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    [Batch {j.id}] {j.filename} ({j.valid_rows} rows) -{" "}
                    {new Date(j.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Field Selection */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Field to Update
          </label>
          <select
            value={selectedField}
            onChange={(e) => {
              setSelectedField(e.target.value);
              setNewValue(""); // reset value
            }}
            className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Select a field --</option>
            {editableColumns.map((c) => (
              <option key={c.name} value={c.name}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Dynamic Value Input */}
        {selectedField && (
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              New Value
            </label>
            {selectedField === "experiment_id" ? (
              <Dropdown
                id="batch-metadata-dropdown"
                options={metadataList.map((m) => ({
                  value: m.experiment_id,
                  label: `[${m.experiment_id}] ${m.title || "No Title"}`,
                }))}
                value={newValue}
                onChange={setNewValue}
                placeholder="a publication"
                emptyLabel="-- None (Clear) --"
              />
            ) : selectedColumn?.type === "boolean" ? (
              <select
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- None (Clear) --</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : selectedField === "status" ? (
               <select
               value={newValue}
               onChange={(e) => setNewValue(e.target.value)}
               className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
             >
               <option value="">-- Select status --</option>
               <option value="draft">Draft</option>
               <option value="published">Published</option>
             </select>
            ) : (
              <input
                type={
                  selectedColumn?.type === "integer" ||
                  selectedColumn?.type === "number"
                    ? "number"
                    : "text"
                }
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Enter new value..."
                className="w-full px-3 py-2 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onClose}
          disabled={applying}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApply}
          disabled={
            !selectedField ||
            applying ||
            (mode === "batch" && !selectedJobId) ||
            (mode === "selected" && (!selectedPks || selectedPks.size === 0))
          }
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {applying ? (
            <>
              <LoadingSpinner /> Applying...
            </>
          ) : (
            "Apply Changes"
          )}
        </button>
      </div>
    </Backdrop>
  );
};

export default BulkEditModal;
